import { useCallback, useEffect, useState } from "react";
import type { AdminEvent, AdminSeries } from "@shared";
import * as api from "./adminApi";
import AppObjectsPanel from "./AppObjectsPanel";
import ContestPanel from "./ContestPanel";
import EventsPanel from "./EventsPanel";
import RealObjectsPanel from "./RealObjectsPanel";
import SeriesPanel from "./SeriesPanel";
import TextsPanel from "./TextsPanel";
import UsersPanel from "./UsersPanel";

type Tab = "events" | "texts" | "series" | "appObjects" | "realObjects" | "users" | "contest";

const TABS: Array<{ id: Tab; label: string }> = [
  // Textes en tete : c'est ce qu'on retouche le plus souvent, et la mission
  // active etant selectionnee d'emblee, on peut y travailler sans detour.
  { id: "texts", label: "Textes" },
  { id: "events", label: "Missions" },
  { id: "series", label: "Séries" },
  { id: "appObjects", label: "Objets" },
  { id: "realObjects", label: "Codes" },
  { id: "users", label: "Joueurs" },
  // En dernier : c'est le seul onglet qui manipule une donnee personnelle, on
  // ne tombe pas dessus en cherchant autre chose.
  { id: "contest", label: "Concours" },
];

/**
 * Cœur de l'administration, affiché une fois le mot de passe validé.
 *
 * Les onglets sont EN HAUT et collants : ils étaient auparavant sous le
 * formulaire d'événement, donc repoussés hors de l'écran dès qu'on ouvrait une
 * modification — on ne les trouvait plus. La gestion des missions est devenue
 * un onglet comme les autres.
 */
export default function AdminApp() {
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [series, setSeries] = useState<AdminSeries[]>([]);
  const [tab, setTab] = useState<Tab>("texts");

  // Les séries sont chargées ici plutôt que dans chaque panneau : les objets
  // et les codes en ont besoin pour leurs listes déroulantes.
  const reloadSeries = useCallback(async () => {
    if (event === null) {
      setSeries([]);
      return;
    }
    try {
      setSeries(await api.listSeries(event.id));
    } catch {
      setSeries([]);
    }
  }, [event]);

  useEffect(() => {
    void reloadSeries();
  }, [reloadSeries]);

  // La mission active est sélectionnée d'emblée : dans la quasi-totalité des
  // cas c'est celle sur laquelle on vient travailler.
  useEffect(() => {
    let cancelled = false;
    api
      .listEvents()
      .then((events) => {
        const active = events.find((e) => e.isActive) ?? events[0] ?? null;
        if (!cancelled && active !== null) setEvent(active);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const needsEvent = tab !== "events" && event === null;

  return (
    <div className="admin">
      <nav className="tabs tabs--sticky" aria-label="Sections de l'administration">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`tabs__button ${tab === entry.id ? "tabs__button--active" : ""}`}
            onClick={() => {
              setTab(entry.id);
            }}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      <p className="admin__context">
        {event === null ? (
          "Aucune mission sélectionnée"
        ) : (
          <>
            Mission : <strong>{event.name}</strong>
            {!event.isActive && " (archivée)"}
          </>
        )}
      </p>

      {tab === "events" && (
        <EventsPanel
          selectedId={event?.id ?? null}
          onSelect={(next) => {
            setEvent(next);
            // Choisir une mission enchaîne naturellement sur ses textes.
            if (next !== null) setTab("texts");
          }}
        />
      )}

      {needsEvent && (
        <p className="panel__hint panel__hint--standalone">
          Choisis d'abord une mission dans l'onglet « Missions ».
        </p>
      )}

      {event !== null && (
        <>
          {tab === "texts" && <TextsPanel eventId={event.id} />}
          {tab === "series" && (
            <SeriesPanel eventId={event.id} onChanged={() => void reloadSeries()} />
          )}
          {tab === "appObjects" && (
            <AppObjectsPanel
              eventId={event.id}
              series={series}
              onChanged={() => void reloadSeries()}
            />
          )}
          {tab === "realObjects" && <RealObjectsPanel eventId={event.id} series={series} />}
          {tab === "users" && (
            <UsersPanel eventId={event.id} contestEnabled={event.contestEnabled} />
          )}
          {tab === "contest" && (
            <ContestPanel eventId={event.id} enabled={event.contestEnabled} />
          )}
        </>
      )}
    </div>
  );
}
